import { Box, Checkbox } from "@mui/material"
import Modal from "./Modal"
import { PoolDataType, UserShareType } from "../hooks/usePoolData"
import React, { ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"
import { DepositTransaction } from "../interfaces/transactions"
import CroWarning from "./CroWarning"
import ReviewDeposit from "./ReviewDeposit"
import {
  commify,
  formatBNToPercentString,
  formatBNToString,
  toSignificant,
} from "../utils"
import { logEvent } from "../utils/googleAnalytics"
import CustomCard from "./CustomCard"
import AmountInput from "./AmountInput"
import { usePoolTokenBalances } from "src/state/wallet/hooks"
import { BIG_ZERO } from "src/constants/bigNumber"
import Text from "./Text"
import Button from "./Button"
import { POOLS_MAP, isMetaPool, CRO, LCRO } from "src/constants"
import SlippageRow from "./SlippageRow"
import { useActiveWeb3React } from "src/hooks"
import Web3Status from "./Web3Status"
import { TransactionSummaryData } from "./TransactionSummary"
import { useSelector } from "react-redux"
import { AppState } from "src/state"
import { subtractSlippage } from "src/utils/slippage"

/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  title: string
  onConfirmTransaction: (summary: TransactionSummaryData) => Promise<void>
  onChangeTokenInputValue: (tokenSymbol: string, value: string) => void
  onToggleDepositWrapped: () => void
  shouldDepositWrapped: boolean
  tokens: Array<{
    symbol: string
    name: string
    icon: string
    max: string
    inputValue: string
    decimals: number
  }>
  exceedsWallet: boolean
  selected?: { [key: string]: any }
  poolData: PoolDataType
  myShareData: UserShareType | undefined
  transactionData: DepositTransaction
}

/* eslint-enable @typescript-eslint/no-explicit-any */
const DepositPage = (props: Props): ReactElement => {
  const { t } = useTranslation()
  const {
    tokens,
    exceedsWallet,
    poolData,
    // myShareData,
    transactionData,
    shouldDepositWrapped,
    onChangeTokenInputValue,
    onConfirmTransaction,
    onToggleDepositWrapped,
  } = props
  const { slippageCustom, slippageSelected } = useSelector(
    (state: AppState) => state.user,
  )
  const { account } = useActiveWeb3React()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const tokenBalances = usePoolTokenBalances()
  const validDepositAmount = transactionData.to.totalAmount.gt(0)
  const pool = POOLS_MAP[poolData.name || ""]

  const minReceive = validDepositAmount
    ? commify(
        toSignificant(
          formatBNToString(
            subtractSlippage(
              transactionData.to.item.amount,
              slippageSelected,
              slippageCustom,
            ),
            transactionData.to.item.token.decimals,
          ),
          6,
        ),
      )
    : "0"

  const summary = [
    ...transactionData.from.items.map(({ token, amount }) => ({
      label: t("Send"),
      amount: commify(formatBNToString(amount, token.decimals)),
      token,
    })),
    {
      label: t("Receive (Min)"),
      amount: minReceive,
      token: transactionData.to.item.token,
    },
  ]

  return (
    <Box>
      <Box>
        {tokens.map((token) => (
          <CustomCard key={token.symbol} variant="black50" mt={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" width="35%" mr={1}>
                <img src={token.icon} height={16} />
                <Text ml={1}>{token.symbol}</Text>
              </Box>
              <Box width="65%" pb={3}>
                <AmountInput
                  key={token.symbol}
                  balance={tokenBalances?.[token.symbol] || BIG_ZERO}
                  value={token.inputValue}
                  decimals={token.decimals}
                  tokenSymbol={token.symbol}
                  onChange={(val) => onChangeTokenInputValue(token.symbol, val)}
                  showMax
                  hidePercentageButtons
                />
              </Box>
            </Box>
            {token.symbol === CRO.symbol && (
              <CroWarning
                mt={0}
                content="Deposited CRO will be wrapped as WCRO."
              />
            )}
            {token.symbol === LCRO.symbol && (
              <CroWarning
                mt={0}
                content="LCRO provided as liquidity will continue to accrue staking rewards."
                link=""
              />
            )}
          </CustomCard>
        ))}
      </Box>
      {isMetaPool(poolData.name) && (
        <Box mt={1} display="flex" alignItems="center">
          <Checkbox
            checked={shouldDepositWrapped}
            onChange={onToggleDepositWrapped}
          />
          {t("Deposit wrapped")}
        </Box>
      )}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Text>{t("Receive Min (LP)")}</Text>
        <Box display="flex" alignItems="center">
          <Box>{minReceive}</Box>
          <Box mr={1} ml={0.5}>
            {poolData?.lpToken}
          </Box>
          {pool && <img src={pool.lpToken.icon} height={16} />}
        </Box>
      </Box>
      <Box my={1}>
        <SlippageRow />
      </Box>
      <Box mt={2} mb={3}>
        <Text
          gradient={
            transactionData.priceImpact.gte(0) ? "gradient1" : "gradient-orange"
          }
          borderRadius="10px"
          py={0.5}
          px={2}
          size="micro"
        >
          {transactionData.priceImpact.gte(0)
            ? t("Bonus: ")
            : t("Price Impact: ")}
          {transactionData.priceImpact.gt(0) ? "+" : ""}
          {formatBNToPercentString(transactionData.priceImpact, 18, 4)}
        </Text>
      </Box>
      {account ? (
        <Button
          fullWidth
          disabled={!validDepositAmount || poolData?.isPaused || exceedsWallet}
          onClick={() => setShowConfirmModal(true)}
        >
          {exceedsWallet ? t("Insufficient Balance") : t("Add Liquidity")}
        </Button>
      ) : (
        <Web3Status fullWidth />
      )}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t("Confirm Transaction")}
      >
        <ReviewDeposit
          transactionSummary={summary}
          onConfirm={async (): Promise<void> => {
            setShowConfirmModal(false)
            logEvent("deposit", (poolData && { pool: poolData?.name }) || {})
            await onConfirmTransaction(summary)
          }}
        />
      </Modal>
    </Box>
  )
}

export default DepositPage
